import { SpaErrorLabels } from "../../mqttData/errors/spaErrorLabels.enums";
import { SpaStatusLabels } from "../../mqttData/status/spaStatusLabels.enums";
import { ArcticLabels } from "../SpaErrors/components/arcticLabels.enums";
import { LiveReportLabels } from "./reportEnums.enums";
import { palName, localizePal } from "../../utils/brand";

const sortOptions = (options:any[])=>{
  return options.sort((a:any,b:any)=>{return a.label===b.label ? 0 :a.label>b.label ? 1 : -1})
}
export const liveOptions = sortOptions([
    {id:`STemp`, label:LiveReportLabels[`STemp`]},
    {id:`TSP`, label:LiveReportLabels[`TSP`]},
    {id:`HTemp`, label:LiveReportLabels[`HTemp`]},
    {id:`SBConnected`, label:localizePal(LiveReportLabels[`SBConnected`])},
    {id:`Filter`, label:LiveReportLabels[`Filter`]},
    {id:`P1`, label:LiveReportLabels[`P1`]},
    {id:`H1`, label:LiveReportLabels[`H1`]},
    {id:`Current`, label:LiveReportLabels[`Current`]},
    {id:`Econ`, label:LiveReportLabels[`Econ`]},
    {id:`sbVin`, label:LiveReportLabels[`sbVin`]},
    {id:`sbVout`, label:LiveReportLabels[`sbVout`]},
    {id:`sbI1`, label:LiveReportLabels[`sbI1`]},
    {id:`sbI2`, label:LiveReportLabels[`sbI2`]},
    {id:`sbpH`, label:LiveReportLabels[`sbpH`]},
    {id:`sbORP`, label:LiveReportLabels[`sbORP`]},
    {id:`sbE1p`, label:LiveReportLabels[`sbE1p`]},
    {id:`sbE1n`, label:LiveReportLabels[`sbE1n`]},
    {id:`sbE2p`, label:LiveReportLabels[`sbE2p`]},
    {id:`sbE2n`, label:LiveReportLabels[`sbE2n`]},
    {id:`sbStat`, label:localizePal(LiveReportLabels[`sbStat`])},
    {id:`sbSM`, label:localizePal(LiveReportLabels[`sbSM`])},
    {id:`sbProducing`, label:localizePal(LiveReportLabels[`sbProducing`])},
    {id:`phSM`, label:LiveReportLabels[`phSM`]},
  ]);

// The live fields the legacy database records for a V1 / Coldfire spa. The report
// offers only these for one; the rest have no V1 history at all.
export const V1_LIVE_FIELDS = [`STemp`,`TSP`,`H1`,`Filter`,`Econ`,`P1`,`Current`,`sbpH`,`sbORP`,`sbI1`,`sbVout`,`SBConnected`,`sbProducing`,`sbE1n`,`sbE1p`,`sbE2n`,`sbE2p`,`sbEState`,`sbEPol`,`sbEmAh`];
export const v1LiveOptions = sortOptions([
    ...liveOptions.filter((opt: any) => V1_LIVE_FIELDS.includes(opt.id)),
    {id:`sbEState`, label:`Electrode State`},
    {id:`sbEPol`, label:`Electrode Polarity`},
    {id:`sbEmAh`, label:`Electrode mAh`},
  ]);

// The classic Spa Boy table, column for column, under its own headers. The V1 Spa Boy
// Table report shows exactly this.
export const V1_SPABOY_TABLE_COLUMNS = [
    {key:`sbORP`, label:`ORP`},
    {key:`sbpH`, label:`PH`},
    {key:`sbI1`, label:`i`},
    {key:`sbVout`, label:`V`},
    {key:`sbEState`, label:`E-State`},
    {key:`sbEPol`, label:`E-Pol`},
    {key:`sbE1n`, label:`E1-N`},
    {key:`sbE1p`, label:`E1-P`},
    {key:`sbE2n`, label:`E2-N`},
    {key:`sbE2p`, label:`E2-P`},
    {key:`sbEmAh`, label:`E-mAh`},
    {key:`timestamp`, label:`Time Stamp`},
  ];

export const settingsOptions = sortOptions([
    {id:`Private_IP`, label:`Private IP`},
    {id:`Public_IP`, label:`Public IP`},
    {id:`SBConnected`, label:`${palName} Connection`},
    {id:`RFIDConnected`, label:`RFID Connection`},
  ]);

export const firmwareOptions = sortOptions([
    {id:`LPCFWVer`, label:`LPC Versions`},
    {id:`SBFWVer`, label:`${palName} Versions`},
    {id:`YOCFWVer`, label:`Yocto Versions`},
  ]);

export const RFIDOptions = sortOptions([
    {id:`tag1`, label:`Tag 1`},
    {id:`tag2`, label:`Tag 2`},
  ]);

export const spaSetOptions = sortOptions([
    {id:`TSP`, label:`Temperature Set Point`},
    {id:`FF`, label:`Filter Frequency`},
    {id:`OnHr`, label:`Onzen Hours`},
    {id:`OnCy`, label:`Onzen Cycles`},
    {id:`OzHr`, label:`Ozone Hours`},
    {id:`OzCy`, label:`Ozone Cycles`},
    {id:`SBHr`, label:`${palName} Hours`},
    {id:`Toff`, label:`Temperature Offset`},
    {id:`FS`, label:`Filter Suspend`},
    {id:`FD`, label:`Filter Duration`},
  ]);

export const spaBoyOptions = sortOptions([
    {id:`SBORPhi`, label:`ORP High`},
    {id:`SBORPlo`, label:`ORP Low`},
    {id:`SBpHhi`, label:`pH High`},
    {id:`SBpHlo`, label:`pH Low`},
  ]);

export const errorOptions = Object.values(SpaErrorLabels).map((err)=>(
  {id:err, label:ArcticLabels[err]!==""?`${err} - ${localizePal(ArcticLabels[err])}`:`${err}`}
));

export const statusOptions = Object.values(SpaStatusLabels).map((stat)=>(
  {id:stat, label:ArcticLabels[stat]!==""?`${stat} - ${localizePal(ArcticLabels[stat])}`:`${stat}`}
));

export const connectionOptions = sortOptions([
    {id:`versionNumber`, label:`Version Number`},
    {id:`ipAddress`, label:`IP Address`},
    {id:`disconnectReason`, label:`Disconnected Reason`},
    {id:`eventType`, label:`Event Type`},
  ]);

// V1 records connects and disconnects only: no firmware version or disconnect reason.
export const v1ConnectionOptions = connectionOptions.filter((opt: any) => [`ipAddress`, `eventType`].includes(opt.id));

// Yocto-class V1 packs: SPA_NA/EURO_YOCTUB and COLD_NA/EURO_YOCTUB. A spa with no product
// code counts as SPA_NA_YOCTUB, as on the classic portal. Only these have bitmask errors;
// Coldfire packs report the Spa_Error flags, whose history is too large to report on.
const V1_YOCTUB_PRODUCT_CODES = [65540, 65541, 196610, 196611];
export const isV1Yoctub = (spa: any): boolean =>
    V1_YOCTUB_PRODUCT_CODES.includes(Number(spa?.product_code ?? 65540));

// V1 (Yocto-class) bitmask errors under the classic details page's names, in bit order,
// numbered as ERRxx the way the details page shows them: ERR00 FLO, and ERR59 for a
// reserved bit that has only a number.
const V1_ERROR_NAMES: { [bit: number]: string } = {
    0: `FLO`, 1: `FLC`, 2: `HL`, 3: `HL (F)`, 4: `Prr`, 5: `HL`, 6: `EE`, 7: `FP`,
    8: `pH`, 9: `Hd`, 10: `HPt`, 11: `SB-CE`, 12: `H/S cal`, 13: `H-wo-S`, 14: `orp_nr`,
    15: `Ph_L`, 20: `P1Lnc`, 21: `P1Hnc`, 22: `P2nc`, 23: `P3nc`, 24: `SDSnc`, 25: `YESSnc`,
    26: `Onnc`, 27: `H1nc`, 28: `H2nc`,
};
export const v1ErrorOptions = Array.from({length: 64}, (_, bit) => {
    const code = `ERR${String(bit).padStart(2, '0')}`;
    const id = V1_ERROR_NAMES[bit] ? `${code} ${V1_ERROR_NAMES[bit]}` : code;
    return {id, label: id};
});

export const fieldHistoryOptions = sortOptions([
    {id:`user_id`, label:`User`},
    {id:`dealership_id`, label:`Dealership`},
  ]);

export const commandHistoryOptions = sortOptions([
    {id:`commandHistory`, label:`Command History`},
  ]);

export const upgradeRequestHistoryOptions = sortOptions([
    {id:`upgradeRequests`, label:`Upgrade Request History`},
  ]);